import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Tab,
  Tabs,
  TextField,
} from '@mui/material';
import { type ReactNode, useState } from 'react';

import {
  isClosedStatus,
  orderStatuses,
  STATUS_COLOR,
  STATUS_COLORS,
} from '~/components/tasks/taskStyle';
import type {
  TaskLogLabelWithCount,
  TaskStatusColorEnum,
  TaskStatusWithCount,
} from '~/data/generated/model';
import { useTaskLogLabelActions, useTaskLogLabels } from '~/data/hooks/useTaskLogLabels';
import { useTaskStatusActions, useTaskStatuses } from '~/data/hooks/useTaskStatuses';
import { LiftAPIClientError } from '~/data/liftApiMutator';
import { BUTTON_SX } from '~/theme';

function apiErrorMessage(error: unknown, fallback: string): string {
  const data = error instanceof LiftAPIClientError ? error.context.data : null;
  if (data && typeof data === 'object') {
    const messages = Object.values(data as Record<string, unknown>).flatMap((value) =>
      Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [],
    );
    if (messages.length > 0) return messages.join(' ');
  }
  return fallback;
}

function ColorSelect({
  value,
  onChange,
}: {
  value: TaskStatusColorEnum;
  onChange: (color: TaskStatusColorEnum) => void;
}) {
  return (
    <TextField
      select
      size="small"
      value={value}
      onChange={(e) => onChange(e.target.value as TaskStatusColorEnum)}
      slotProps={{ htmlInput: { 'aria-label': 'Color' } }}
      sx={{ width: 76 }}
    >
      {STATUS_COLORS.map((color) => (
        <MenuItem key={color} value={color} aria-label={color}>
          <Box
            sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: STATUS_COLOR[color].solid }}
          />
        </MenuItem>
      ))}
    </TextField>
  );
}

function NameField({
  initial,
  label,
  onCommit,
}: {
  initial: string;
  label: string;
  onCommit: (name: string) => Promise<boolean>;
}) {
  const [name, setName] = useState(initial);
  const commit = async () => {
    const trimmed = name.trim();
    if (!trimmed) setName(initial);
    else if (trimmed !== initial && !(await onCommit(trimmed))) setName(initial);
  };
  return (
    <TextField
      size="small"
      value={name}
      onChange={(e) => setName(e.target.value)}
      onBlur={() => void commit()}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
      }}
      slotProps={{ htmlInput: { 'aria-label': label, maxLength: 40 } }}
      sx={{ flex: 1 }}
    />
  );
}

function SortableRow({
  id,
  name,
  testId,
  children,
}: {
  id: string;
  name: string;
  testId: string;
  children: ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });
  return (
    <Box
      ref={setNodeRef}
      data-testid={testId}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        py: '6px',
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
        bgcolor: 'background.paper',
      }}
    >
      <IconButton
        size="small"
        aria-label={`Reorder ${name}`}
        {...attributes}
        {...listeners}
        sx={{ cursor: 'grab', touchAction: 'none' }}
      >
        <DragIndicatorIcon fontSize="small" />
      </IconButton>
      {children}
    </Box>
  );
}

function RemoveStatusDialog({
  status,
  others,
  onCancel,
  onConfirm,
}: {
  status: TaskStatusWithCount;
  others: TaskStatusWithCount[];
  onCancel: () => void;
  onConfirm: (moveToId: string | null) => void;
}) {
  const [moveToId, setMoveToId] = useState('');
  const needsMove = status.taskCount > 0;
  const canConfirm = !needsMove || Boolean(moveToId);
  return (
    <Dialog open onClose={onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>Remove “{status.name}”</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
        {needsMove ? (
          <>
            <Box sx={{ fontSize: 14 }}>
              {status.taskCount} {status.taskCount === 1 ? 'task is' : 'tasks are'} in this status.
              Move {status.taskCount === 1 ? 'it' : 'them'} to:
            </Box>
            <TextField
              select
              size="small"
              required
              label="Move tasks to"
              value={moveToId}
              onChange={(e) => setMoveToId(e.target.value)}
            >
              {others.map((other) => (
                <MenuItem key={other.id} value={other.id}>
                  {other.name}
                </MenuItem>
              ))}
            </TextField>
          </>
        ) : (
          <Box sx={{ fontSize: 14 }}>No tasks use this status.</Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="outlined" onClick={onCancel} sx={BUTTON_SX}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          disabled={!canConfirm}
          onClick={() => onConfirm(needsMove ? moveToId : null)}
          sx={BUTTON_SX}
        >
          Remove status
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function RemoveLabelDialog({
  label,
  onCancel,
  onConfirm,
}: {
  label: TaskLogLabelWithCount;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <Dialog open onClose={onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>Remove “{label.name}”</DialogTitle>
      <DialogContent sx={{ fontSize: 14 }}>
        {label.logCount === 0
          ? 'No update logs use this label.'
          : label.logCount === 1
            ? '1 update log uses this label. It will keep the label, but nobody can pick it for new updates.'
            : `${label.logCount} update logs use this label. They will keep the label, but nobody can pick it for new updates.`}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="outlined" onClick={onCancel} sx={BUTTON_SX}>
          Cancel
        </Button>
        <Button variant="contained" color="error" onClick={onConfirm} sx={BUTTON_SX}>
          Remove label
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function UsageCount({ count, noun }: { count: number; noun: string }) {
  return (
    <Box sx={{ width: 110, fontSize: 12, color: 'text.secondary', textAlign: 'right' }}>
      {count} {count === 1 ? noun : `${noun}s`}
    </Box>
  );
}

function AddRow({
  defaultColor,
  label,
  placeholder,
  onAdd,
  extra,
  onCancel,
}: {
  defaultColor: TaskStatusColorEnum;
  label: string;
  placeholder: string;
  onAdd: (body: { name: string; color: TaskStatusColorEnum }) => Promise<boolean>;
  extra?: ReactNode;
  onCancel?: () => void;
}) {
  const [name, setName] = useState('');
  const [color, setColor] = useState<TaskStatusColorEnum>(defaultColor);
  const add = async () => {
    if (!name.trim()) return;
    if (await onAdd({ name: name.trim(), color })) setName('');
  };
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', py: '6px', pl: '40px' }}>
      <ColorSelect value={color} onChange={setColor} />
      <TextField
        size="small"
        value={name}
        placeholder={placeholder}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') void add();
          if (e.key === 'Escape') onCancel?.();
        }}
        slotProps={{
          htmlInput: { 'aria-label': `New ${label.toLowerCase()} name`, maxLength: 40 },
        }}
        sx={{ flex: 1 }}
      />
      {extra}
      <Button onClick={() => void add()} disabled={!name.trim()} sx={BUTTON_SX}>
        Add {label.toLowerCase()}
      </Button>
      {onCancel ? (
        <Button onClick={onCancel} sx={BUTTON_SX}>
          Cancel
        </Button>
      ) : null}
    </Box>
  );
}

function attemptWith(onError: (message: string | null) => void) {
  return async (write: () => Promise<unknown>, fallback: string) => {
    onError(null);
    try {
      await write();
      return true;
    } catch (err) {
      onError(apiErrorMessage(err, fallback));
      return false;
    }
  };
}

function useRowSensors() {
  return useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
}

function StatusesTab({ onError }: { onError: (message: string | null) => void }) {
  const { statuses } = useTaskStatuses();
  const { addStatus, updateStatus, reorderStatuses, archiveStatus } = useTaskStatusActions();
  const [removing, setRemoving] = useState<TaskStatusWithCount | null>(null);
  const sensors = useRowSensors();
  const ordered = orderStatuses(statuses);
  const attempt = attemptWith(onError);

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const ids = ordered.map((s) => s.id);
    const next = arrayMove(ids, ids.indexOf(String(active.id)), ids.indexOf(String(over.id)));
    void attempt(() => reorderStatuses(next), "Couldn't reorder the statuses.");
  };

  return (
    <>
      <Box sx={{ fontSize: 13, color: 'text.secondary', pb: '8px' }}>
        The steps your team moves tasks through. New tasks start in the first open one. Completed
        and Canceled close a task and stay in every workflow.
      </Box>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={ordered.map((s) => s.id)} strategy={verticalListSortingStrategy}>
          {ordered.map((status) => (
            <SortableRow
              key={`${status.id}:${status.name}`}
              id={status.id}
              name={status.name}
              testId="status-row"
            >
              <ColorSelect
                value={status.color}
                onChange={(color) =>
                  void attempt(
                    () => updateStatus(status.id, { color }),
                    "Couldn't save the status.",
                  )
                }
              />
              {isClosedStatus(status) ? (
                <Box
                  sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px', px: '14px' }}
                >
                  <Box sx={{ fontSize: 14 }}>{status.name}</Box>
                  <Box sx={{ fontSize: 12, color: 'text.secondary' }}>Closes the task</Box>
                </Box>
              ) : (
                <NameField
                  initial={status.name}
                  label="Status name"
                  onCommit={(name) =>
                    attempt(() => updateStatus(status.id, { name }), "Couldn't save the status.")
                  }
                />
              )}
              <UsageCount count={status.taskCount} noun="task" />
              {isClosedStatus(status) ? (
                <Box sx={{ width: 34 }} />
              ) : (
                <IconButton
                  size="small"
                  aria-label={`Remove ${status.name}`}
                  onClick={() => setRemoving(status)}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              )}
            </SortableRow>
          ))}
        </SortableContext>
      </DndContext>
      <AddRow
        defaultColor="grey"
        label="Status"
        placeholder="New status"
        onAdd={(body) => attempt(() => addStatus(body), "Couldn't add the status.")}
      />
      {removing ? (
        <RemoveStatusDialog
          status={removing}
          others={ordered.filter((s) => s.id !== removing.id)}
          onCancel={() => setRemoving(null)}
          onConfirm={(moveToId) => {
            const target = removing;
            setRemoving(null);
            void attempt(() => archiveStatus(target.id, moveToId), "Couldn't remove the status.");
          }}
        />
      ) : null}
    </>
  );
}

function LabelsTab({ onError }: { onError: (message: string | null) => void }) {
  const { labels } = useTaskLogLabels();
  const { addLabel, updateLabel, reorderLabels, archiveLabel } = useTaskLogLabelActions();
  const [removing, setRemoving] = useState<TaskLogLabelWithCount | null>(null);
  const sensors = useRowSensors();
  const attempt = attemptWith(onError);

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const ids = labels.map((l) => l.id);
    const next = arrayMove(ids, ids.indexOf(String(active.id)), ids.indexOf(String(over.id)));
    void attempt(() => reorderLabels(next), "Couldn't reorder the labels.");
  };

  const save = (label: TaskLogLabelWithCount, changes: Parameters<typeof updateLabel>[1]) =>
    attempt(() => updateLabel(label.id, changes), "Couldn't save the label.");

  return (
    <>
      <Box sx={{ fontSize: 13, color: 'text.secondary', pb: '8px' }}>
        The labels your team picks from when logging an update. Removing a label keeps it on past
        updates.
      </Box>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={labels.map((l) => l.id)} strategy={verticalListSortingStrategy}>
          {labels.map((label) => (
            <SortableRow
              key={`${label.id}:${label.name}`}
              id={label.id}
              name={label.name}
              testId="label-row"
            >
              <ColorSelect value={label.color} onChange={(color) => void save(label, { color })} />
              <NameField
                initial={label.name}
                label="Label name"
                onCommit={(name) => save(label, { name })}
              />
              <UsageCount count={label.logCount} noun="update log" />
              <IconButton
                size="small"
                aria-label={`Remove ${label.name}`}
                onClick={() => setRemoving(label)}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </SortableRow>
          ))}
        </SortableContext>
      </DndContext>
      <AddRow
        defaultColor="grey"
        label="Label"
        placeholder="New label"
        onAdd={(body) => attempt(() => addLabel(body), "Couldn't add the label.")}
      />
      {removing ? (
        <RemoveLabelDialog
          label={removing}
          onCancel={() => setRemoving(null)}
          onConfirm={() => {
            const target = removing;
            setRemoving(null);
            void attempt(() => archiveLabel(target.id), "Couldn't remove the label.");
          }}
        />
      ) : null}
    </>
  );
}

type WorkflowTab = 'statuses' | 'labels';

export function StatusesDialog({ open, onClose: close }: { open: boolean; onClose: () => void }) {
  const [tab, setTab] = useState<WorkflowTab>('statuses');
  const [error, setError] = useState<string | null>(null);
  const onClose = () => {
    setError(null);
    close();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" aria-labelledby="workflow-title">
      <DialogTitle id="workflow-title" sx={{ pr: 6, pb: 0 }}>
        Customize workflow
      </DialogTitle>
      <IconButton
        aria-label="Close"
        onClick={onClose}
        sx={{ position: 'absolute', right: 12, top: 12 }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
      <Tabs
        value={tab}
        onChange={(_event, next: WorkflowTab) => {
          setError(null);
          setTab(next);
        }}
        aria-label="Workflow sections"
        sx={{ px: 3, borderBottom: '1px solid', borderColor: 'divider' }}
      >
        <Tab value="statuses" label="Task status lines" />
        <Tab value="labels" label="Log update labels" />
      </Tabs>
      <DialogContent>
        {error ? (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 1 }}>
            {error}
          </Alert>
        ) : null}
        {tab === 'statuses' ? <StatusesTab onError={setError} /> : <LabelsTab onError={setError} />}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="contained" onClick={onClose} sx={BUTTON_SX}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}
